import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useAppwideDrawerContext } from '../../../../providers/AppwideDrawerProvider';
import { EMPTY_ARRAY } from '../../../../utils/empty';
import { Button, ButtonVariants } from '../../../Button';
import { Box, HStack } from '../../../Core';
import { useEntityAdminFormState } from '../../hooks/useEntityAdminFormState';
import type { EntityAdminRecord } from '../../types';
import { DrawerOptionsList } from '../InputsAndDropdownsDrawer/components/DrawerOptionsList';
import { registerDefaultDrawerContentRenderers } from '../InputsAndDropdownsDrawer/components/registerDrawerContent';
import type { InputsAndDropdownsDrawerOptions } from '../InputsAndDropdownsDrawer/types';

registerDefaultDrawerContentRenderers();

interface WorkflowFormStepProps<TForm extends EntityAdminRecord> {
  drawerOptions: InputsAndDropdownsDrawerOptions<TForm>;
  initialForm: TForm;
  summary?: ReactNode;
  saveLabel: string;
  saveVariant?: ButtonVariants;
  loading?: boolean;
  onCancel: () => void;
  onSave: (preparedForm: TForm) => Promise<void> | void;
  onFormStateChange: (form: TForm) => void;
}

export function WorkflowFormStep<TForm extends EntityAdminRecord>({
  drawerOptions,
  initialForm,
  summary,
  saveLabel,
  saveVariant = ButtonVariants.Primary,
  loading = false,
  onCancel,
  onSave,
  onFormStateChange,
}: WorkflowFormStepProps<TForm>) {
  const { isGuideVisible } = useAppwideDrawerContext();

  const {
    form,
    handleOnFormUpdate,
    inheritedValues,
    someError,
    prepareFormForSave,
    errorState,
    touchState,
    touchAll,
    originalEntity,
  } = useEntityAdminFormState<TForm, TForm>({
    selectedEntities: EMPTY_ARRAY,
    drawerOptions,
    getEntityAsDrawerEntity: (entity: TForm) => entity,
    setIsEntityAdminDrawerDirty: () => undefined,
    newEntityDefault: () => initialForm,
    isEditing: false,
  });
  const [canValidateOnSaveHover, setCanValidateOnSaveHover] = useState(false);

  useEffect(() => {
    onFormStateChange(form);
  }, [form, onFormStateChange]);

  useEffect(() => {
    setCanValidateOnSaveHover(false);
    const timeout = window.setTimeout(() => setCanValidateOnSaveHover(true), 150);
    return () => window.clearTimeout(timeout);
  }, [drawerOptions]);

  const handleSave = useCallback(async () => {
    touchAll();
    if (someError || loading) {
      return;
    }
    await onSave(prepareFormForSave());
  }, [loading, onSave, prepareFormForSave, someError, touchAll]);

  const handleSaveHover = useCallback(() => {
    if (canValidateOnSaveHover) {
      touchAll();
    }
  }, [canValidateOnSaveHover, touchAll]);

  return (
    <>
      <Box flex="1" minHeight={0} overflowY="auto">
        <Box p="spacing16">
          {summary ? <Box pb="spacing16">{summary}</Box> : null}
          <DrawerOptionsList
            drawerOptions={drawerOptions}
            form={form}
            originalEntity={originalEntity}
            inheritedValues={inheritedValues}
            errorState={errorState}
            touchState={touchState}
            onFormUpdate={handleOnFormUpdate}
            isEditing={false}
            isGuideVisible={isGuideVisible}
          />
        </Box>
      </Box>
      <HStack
        flex="none"
        gap="spacing12"
        p="spacing12"
        position="sticky"
        bottom="0"
        zIndex={1}
        borderTop="1px solid"
        borderColor="colors.gray.030"
        background="backgroundDrawer"
      >
        <Button onClick={onCancel}>Cancel</Button>
        <Box flex="1" onMouseEnter={handleSaveHover}>
          <Button width="100%" variant={saveVariant} onClick={handleSave} disabled={someError} loading={loading}>
            {saveLabel}
          </Button>
        </Box>
      </HStack>
    </>
  );
}
